import { useState, useEffect, useRef } from "react";
import { Search, X } from "lucide-react";
import { useSettingsSearch } from "../hooks/useSettingsSearch";
import SettingCard from "./SettingCard";
import { Input } from "@/modern/components/ui/input";
import { Button } from "@/modern/components/ui/button";
import { cn } from "@/modern/utils";
import { useSettingsContext } from "../context/SettingsContext";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/modern/components/ui/resizable";

const SettingsContent = () => {
  const { selectedCategory: category, categories, selectedSubcategory, setSelectedSubcategory, isPreviewMode } = useSettingsContext();
  const [searchQuery, setSearchQuery] = useState("");
  const [isInputDisabled, setIsInputDisabled] = useState(false);
  const prevSearchQueryRef = useRef<string>("");

  const { searchResults, isSearching, resultCount } = useSettingsSearch(searchQuery, categories);

  // Clear search when category changes
  useEffect(() => {
    setSearchQuery("");
  }, [category]);

  // Handle selection based on search state
  useEffect(() => {
    if (isSearching) {
      // User is searching - select first result if exists, otherwise clear selection
      if (searchResults.length > 0) {
        const firstResult = searchResults[0];
        setSelectedSubcategory({
          subcategory: firstResult.subcategory,
          category: firstResult.category
        });
      } else {
        setSelectedSubcategory(null);
      }
    } else if (
      !searchQuery &&
      category &&
      category.subcategories.length > 0 &&
      prevSearchQueryRef.current.length > 0
    ) {
      // User just cleared search - select first item from category (don't run on initial load so URL-driven selection is preserved)
      const firstItem = category.subcategories[0];
      setSelectedSubcategory({
        subcategory: firstItem,
        category: category
      });
    }
    prevSearchQueryRef.current = searchQuery;
  }, [isSearching, searchResults, searchQuery, category, setSelectedSubcategory]);

  // Disable input when category or subcategory changes, then re-enable after timeout
  useEffect(() => {
    if (!isSearching) {
      setIsInputDisabled(true);
      const timeoutId = setTimeout(() => {
        setIsInputDisabled(false);
      }, 300); // Re-enable after 300ms

      return () => clearTimeout(timeoutId);
    }
    return;
  }, [category, selectedSubcategory, isSearching]);

  const handleCardClick = (subcategory: any, parentCategory: any) => {
    setSelectedSubcategory({ subcategory, category: parentCategory });
  };

  // Determine what to display
  const displayItems = isSearching
    ? searchResults
    : category
      ? category.subcategories.map((sub) => ({ subcategory: sub, category }))
      : [];

  const title = isSearching ? "Search Results" : category?.label || "";
  const subtitle = isSearching
    ? `${resultCount} ${resultCount === 1 ? "setting" : "settings"} found` : ""

  return (
    <div className="flex flex-col transition-all duration-300 ease-in-out relative w-full h-full">
      {/* Header with Search */}
      <div className="w-full sticky top-0 z-10 bg-background border-b border-border">
        <div className="flex items-center justify-between gap-6 h-[3.68rem] px-5">
          <div className="flex-1 min-w-0 flex items-end gap-1.5">
            <h1 className="text-lg font-semibold text-foreground">{title}</h1>
            {subtitle && (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
          <div className="relative flex-shrink-0">
            <div className="relative w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                type="text"
                placeholder="Search all settings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={isInputDisabled}
                className="pl-10 pr-10 h-10 bg-background/60 border-border focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 rounded-full hover:bg-accent"
                  title="Clear search"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <ResizablePanelGroup
        orientation="horizontal"
        className="flex-1 overflow-hidden"
      >
        <ResizablePanel id="settings-list" maxSize={"40%"}>
          <div className={cn("overflow-y-auto p-6 h-full")}>
            {isSearching && resultCount === 0 ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">No settings match your search</p>
              </div>
            ) : displayItems.length > 0 ? (
              <div className="grid grid-cols-1 gap-4">
                {displayItems.map(({ subcategory, category: parentCategory }) => (
                  <SettingCard
                    key={`${parentCategory.id}-${subcategory.id}`}
                    subcategory={subcategory}
                    category={parentCategory}
                    onClick={() => handleCardClick(subcategory, parentCategory)}
                    isSelected={selectedSubcategory?.subcategory.id === subcategory.id}
                  />
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">Select a category to view settings</p>
              </div>
            )}
          </div>
        </ResizablePanel>

        {selectedSubcategory && (
          <>
            <ResizableHandle withHandle />

            {/* Right Column - Detail Panel */}
            <ResizablePanel id="settings-detail"  maxSize={"80%"} defaultSize={"70%"}>
              <div
                className={cn(
                  "overflow-y-auto bg-card/30 dark:bg-card/50 h-full animate-in fade-in duration-300",
                  isPreviewMode && "[&>*]:pointer-events-none"
                )}
              >
                {(() => {
                  const DetailComponent = selectedSubcategory.subcategory.component;
                  return (
                    <DetailComponent
                      subcategory={selectedSubcategory.subcategory}
                      categoryId={selectedSubcategory.category.id}
                    />
                  );
                })()}
              </div>
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>
    </div>
  );
};

export default SettingsContent;
